import type {
	AdditionalSubnet,
	CommonNetworkResource,
	CommonNetworkSubnet
} from '../types/gcpProject'

const trimValue = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

export type GcpSubnetFormInput = {
	infrastructureType: string
	nonGkeAddSubnets?: 'yes' | 'no'
	primarySubnet?: string
	controlPlaneSubnet?: string
	secondaryPodsRange?: string
	secondaryServicesRange?: string
	primarySubnetResourceType?: string
	primarySubnetPurpose?: string
	additionalSubnets?: AdditionalSubnet[]
	commonNetworkRequired?: 'yes' | 'no'
	// Only the common VPCs the approver switched on; rows may still be blank.
	commonNetworkResources?: CommonNetworkResource[]
	// Staging/Production copy Common Network from the Develop request as-is.
	commonNetworkInherited?: boolean
	// Staging/Production can't be approved until their Develop request is approved.
	developRequestMissing?: boolean
}

export const normalizeAdditionalSubnets = (
	value: unknown
): AdditionalSubnet[] => {
	if (!Array.isArray(value)) {
		return []
	}

	return value
		.map((item) => {
			if (typeof item === 'string') {
				return { cidr: item.trim(), resourceType: '', purpose: '' }
			}
			if (item && typeof item === 'object') {
				return {
					cidr: typeof item.cidr === 'string' ? item.cidr.trim() : '',
					resourceType:
						typeof item.resourceType === 'string' ? item.resourceType : '',
					purpose: typeof item.purpose === 'string' ? item.purpose : ''
				}
			}
			return { cidr: '', resourceType: '', purpose: '' }
		})
		.filter((item) => item.cidr !== '')
}

export const resolveGcpProjectInfrastructureType = (
	details: Record<string, unknown>
): string => {
	const explicitType = trimValue(details.infrastructureType).toUpperCase()
	if (explicitType === 'NON-GKE') {
		return 'Non-GKE'
	}
	return 'GKE'
}

export const isGkeInfrastructure = (
	details: Record<string, unknown>
): boolean => {
	const type = trimValue(details.infrastructureType).toUpperCase()
	if (!type) {
		return true
	}
	return type === 'GKE'
}

export const isGcpProjectGke = (details: Record<string, unknown>): boolean =>
	details.isGKE === true ||
	details.isGKE === 'true' ||
	isGkeInfrastructure(details)

// Prefix limits for every subnet and range on this form; GCP's smallest subnet is /29.
export const SUBNET_MIN_PREFIX_LENGTH = 8
export const SUBNET_MAX_PREFIX_LENGTH = 29
// Octets are 0-255 without leading zeros; the prefix is plain digits.
const STRICT_OCTET_PATTERN = /^(0|[1-9]\d{0,2})$/
const PREFIX_PATTERN = /^\d{1,2}$/

// A canonical IPv4 network address (no host bits) with a /8-/29 prefix, parsed strictly.
export const isValidCidr = (value: string): boolean => {
	const parts = value.trim().split('/')
	if (parts.length !== 2) {
		return false
	}

	const [ipAddress, prefixPart] = parts
	const octets = ipAddress.split('.')
	if (
		octets.length !== 4 ||
		!octets.every(
			(octet) => STRICT_OCTET_PATTERN.test(octet) && Number(octet) <= 255
		)
	) {
		return false
	}

	if (!PREFIX_PATTERN.test(prefixPart)) {
		return false
	}
	const prefix = Number(prefixPart)
	if (prefix < SUBNET_MIN_PREFIX_LENGTH || prefix > SUBNET_MAX_PREFIX_LENGTH) {
		return false
	}

	const address = octets.reduce(
		(total, octet) => total * 256 + Number(octet),
		0
	)
	return address % 2 ** (32 - prefix) === 0
}

export const VPC_CONNECTOR_RESOURCE_TYPE = 'VPC Connector'

export const isVpcConnectorResourceType = (resourceType?: string): boolean =>
	trimValue(resourceType).toLowerCase() ===
	VPC_CONNECTOR_RESOURCE_TYPE.toLowerCase()

export const isValidVpcConnectorCidr = (cidr: string): boolean => {
	if (!isValidCidr(cidr)) {
		return false
	}

	const [ipAddress, prefix] = cidr.trim().split('/')
	if (prefix !== '28') {
		return false
	}

	const octets = ipAddress.split('.').map(Number)
	return octets[3] % 16 === 0
}

// PSA rules. The form is the only place PSA entries are validated before vending;
// PSA ranges use the shared subnet CIDR check (/8-/29, the PSA range module's limits too).
export const PSA_RESOURCE_TYPE = 'PSA'

export const isPsaResourceType = (resourceType?: string): boolean =>
	trimValue(resourceType).toUpperCase() === PSA_RESOURCE_TYPE

export const getSubnetCidrError = (
	cidr: string,
	resourceType: string,
	label: string
): string | undefined => {
	if (!isValidCidr(cidr)) {
		return `${label} must be a valid IPv4 network from /${SUBNET_MIN_PREFIX_LENGTH} to /${SUBNET_MAX_PREFIX_LENGTH} (e.g., 10.0.1.0/28)`
	}

	if (
		isVpcConnectorResourceType(resourceType) &&
		!isValidVpcConnectorCidr(cidr)
	) {
		return 'VPC Connector subnet must use a valid IPv4 /28 network range'
	}

	return undefined
}

const INHERITED_COMMON_NETWORK_ENVIRONMENTS = ['staging', 'production']
const NON_DEVELOP_PROJECT_SUFFIX = /-(stg|prod)$/i

// Staging and Production take their Common Network from the Develop request.
export const inheritsCommonNetwork = (environment?: string): boolean =>
	INHERITED_COMMON_NETWORK_ENVIRONMENTS.includes(
		trimValue(environment).toLowerCase()
	)

// Project names differ only by suffix: glo-<group>-<alias>-project-<dev|stg|prod>
export const getDevelopProjectName = (projectName?: string): string => {
	const name = trimValue(projectName)
	return NON_DEVELOP_PROJECT_SUFFIX.test(name)
		? name.replace(NON_DEVELOP_PROJECT_SUFFIX, '-dev')
		: ''
}

export const PURPOSE_RESOURCE_TYPES = ['PSA', 'PSC']

export const SUBNET_DEFAULT_PURPOSE = 'N/A'

export const requiresSubnetPurpose = (resourceType?: string): boolean =>
	PURPOSE_RESOURCE_TYPES.includes(trimValue(resourceType).toUpperCase())

export const PSC_RESOURCE_TYPE = 'PSC'

export const isPscResourceType = (resourceType?: string): boolean =>
	trimValue(resourceType).toUpperCase() === PSC_RESOURCE_TYPE

// PSA ranges and PSC subnets are named {group}-{platform_alias}-{env|cmn}-{psa|psc}-{purpose}-..., so the purpose must be valid in a GCP resource name.
const NAMED_PURPOSE_PATTERN = /^[a-z0-9-]{1,12}$/

// Format rule for a purpose that becomes part of a resource name; expects a trimmed, lowercased value.
const getNamedPurposeFormatError = (value: string): string | undefined => {
	if (value === SUBNET_DEFAULT_PURPOSE.toLowerCase()) {
		return 'Purpose cannot be N/A'
	}
	if (!NAMED_PURPOSE_PATTERN.test(value)) {
		return 'Purpose must be 1-12 lowercase letters, digits or hyphens'
	}
	return undefined
}

export const getSubnetPurposeError = (
	resourceType?: string,
	purpose?: string
): string | undefined => {
	if (!requiresSubnetPurpose(resourceType)) {
		return undefined
	}

	const value = trimValue(purpose).toLowerCase()
	if (!value) {
		return 'Purpose is required for PSA and PSC'
	}

	return getNamedPurposeFormatError(value)
}

// The purpose as saved: lowercased for PSA/PSC, "N/A" for anything else.
export const resolveSubnetPurpose = (
	resourceType?: string,
	purpose?: string
): string =>
	requiresSubnetPurpose(resourceType)
		? trimValue(purpose).toLowerCase()
		: SUBNET_DEFAULT_PURPOSE

// Carry a backend-generated subnet name through, so Staging/Production reuse Develop's names.
const keepSubnetName = (subnetName: unknown): { subnetName?: string } => {
	const name = trimValue(subnetName)
	return name ? { subnetName: name } : {}
}

export const normalizeCommonNetworkResources = (
	value: unknown
): CommonNetworkResource[] => {
	if (!Array.isArray(value)) {
		return []
	}

	return value
		.filter((item) => item && typeof item === 'object')
		.map((item) => ({
			vpcName: trimValue(item.vpcName),
			subnets: Array.isArray(item.subnets)
				? item.subnets
						.filter((subnet: unknown) => subnet && typeof subnet === 'object')
						.map((subnet: Record<string, unknown>) => ({
							cidr: trimValue(subnet.cidr),
							resourceType: trimValue(subnet.resourceType),
							purpose: trimValue(subnet.purpose),
							...keepSubnetName(subnet.subnetName)
						}))
						.filter((subnet: CommonNetworkSubnet) => subnet.cidr !== '')
				: []
		}))
		.filter((item) => item.vpcName !== '' && item.subnets.length > 0)
}

// New rows start as PSC, so a row with no CIDR and no purpose is blank.
export const isCommonNetworkSubnetEmpty = (subnet: CommonNetworkSubnet) =>
	!trimValue(subnet.cidr) && !trimValue(subnet.purpose)

export type CommonNetworkSubnetErrors = {
	cidr?: string
	resourceType?: string
	purpose?: string
}

// Blank rows are ignored; partially filled rows must be fixed before approval.
export const getCommonNetworkSubnetErrors = (
	subnet: CommonNetworkSubnet,
	label: string
): CommonNetworkSubnetErrors => {
	if (isCommonNetworkSubnetEmpty(subnet)) {
		return {}
	}

	const cidr = trimValue(subnet.cidr)
	const resourceType = trimValue(subnet.resourceType)

	return {
		cidr: cidr
			? getSubnetCidrError(cidr, resourceType, label)
			: `${label} CIDR is required`,
		resourceType: !resourceType
			? 'Resource type is required'
			: !isPscResourceType(resourceType)
			? 'Common Network subnets must be PSC'
			: undefined,
		purpose: getSubnetPurposeError(resourceType, subnet.purpose)
	}
}

const hasCommonNetworkSubnetErrors = (errors: CommonNetworkSubnetErrors) =>
	Boolean(errors.cidr || errors.resourceType || errors.purpose)

export const isCommonNetworkSubnetComplete = (
	subnet: CommonNetworkSubnet,
	label: string
): boolean =>
	!isCommonNetworkSubnetEmpty(subnet) &&
	!hasCommonNetworkSubnetErrors(getCommonNetworkSubnetErrors(subnet, label))

export const countCompleteCommonNetworkSubnets = (
	subnets: CommonNetworkSubnet[]
): number =>
	subnets.filter((subnet, index) =>
		isCommonNetworkSubnetComplete(subnet, `Subnet ${index + 1}`)
	).length

// Each switched-on VPC needs at least one complete row and no half-filled rows.
// Staging/Production inherit from Develop, which must be approved first.
export const validateCommonNetworkResourcesForApproval = (
	input: GcpSubnetFormInput
): boolean => {
	if (input.commonNetworkInherited) {
		return !input.developRequestMissing
	}

	if (input.commonNetworkRequired !== 'yes') {
		return true
	}

	const resources = input.commonNetworkResources || []
	if (resources.length === 0) {
		return false
	}

	return resources.every(
		(resource) =>
			countCompleteCommonNetworkSubnets(resource.subnets) > 0 &&
			!resource.subnets.some((subnet, index) =>
				hasCommonNetworkSubnetErrors(
					getCommonNetworkSubnetErrors(subnet, `Subnet ${index + 1}`)
				)
			)
	)
}

export const buildCommonNetworkResourcesPayload = (
	input: GcpSubnetFormInput
): CommonNetworkResource[] => {
	if (input.commonNetworkInherited) {
		return normalizeCommonNetworkResources(input.commonNetworkResources)
	}

	if (input.commonNetworkRequired !== 'yes') {
		return []
	}

	return (input.commonNetworkResources || [])
		.map((resource) => ({
			vpcName: resource.vpcName,
			subnets: resource.subnets
				.filter((subnet, index) =>
					isCommonNetworkSubnetComplete(subnet, `Subnet ${index + 1}`)
				)
				.map((subnet) => ({
					cidr: trimValue(subnet.cidr),
					resourceType: trimValue(subnet.resourceType),
					purpose: resolveSubnetPurpose(subnet.resourceType, subnet.purpose),
					...keepSubnetName(subnet.subnetName)
				}))
		}))
		.filter((resource) => resource.subnets.length > 0)
}

export const hasCompleteGkeSubnetDetails = (
	details: Record<string, unknown>
): boolean =>
	Boolean(
		isValidCidr(trimValue(details.primarySubnet)) &&
			isValidCidr(trimValue(details.controlPlaneSubnet)) &&
			isValidCidr(trimValue(details.secondaryPodsRange)) &&
			isValidCidr(trimValue(details.secondaryServicesRange))
	)

export const canApproveGcpProjectSubnets = (
	details: Record<string, unknown>
): boolean => !isGcpProjectGke(details) || hasCompleteGkeSubnetDetails(details)

export const validateSubnetDetailsForApproval = (
	input: GcpSubnetFormInput
): boolean => {
	const isGke = trimValue(input.infrastructureType).toUpperCase() === 'GKE'
	const primarySubnet = trimValue(input.primarySubnet)
	const additionalSubnets = input.additionalSubnets || []
	const hasInvalidAdditionalSubnet = additionalSubnets.some(
		(subnet, index) =>
			subnet.cidr.trim() !== '' &&
			(subnet.resourceType.trim() === '' ||
				Boolean(getSubnetPurposeError(subnet.resourceType, subnet.purpose)) ||
				Boolean(
					getSubnetCidrError(
						subnet.cidr,
						subnet.resourceType,
						`Additional Subnet ${index + 1}`
					)
				))
	)

	const hasValidCommonNetwork = validateCommonNetworkResourcesForApproval(input)

	if (isGke) {
		return (
			hasCompleteGkeSubnetDetails({
				primarySubnet,
				controlPlaneSubnet: input.controlPlaneSubnet,
				secondaryPodsRange: input.secondaryPodsRange,
				secondaryServicesRange: input.secondaryServicesRange
			}) &&
			!hasInvalidAdditionalSubnet &&
			hasValidCommonNetwork
		)
	}

	if (input.nonGkeAddSubnets !== 'yes') {
		return hasValidCommonNetwork
	}

	if (
		!primarySubnet ||
		getSubnetCidrError(
			primarySubnet,
			trimValue(input.primarySubnetResourceType),
			'Primary Subnet'
		)
	) {
		return false
	}

	if (!trimValue(input.primarySubnetResourceType)) {
		return false
	}

	if (
		getSubnetPurposeError(
			input.primarySubnetResourceType,
			input.primarySubnetPurpose
		)
	) {
		return false
	}

	return !hasInvalidAdditionalSubnet && hasValidCommonNetwork
}

export const buildSubnetDetailsPayload = (
	input: GcpSubnetFormInput
): Record<string, unknown> => {
	const infrastructureType = trimValue(input.infrastructureType) || 'GKE'
	const isGke = infrastructureType.toUpperCase() === 'GKE'
	const primarySubnet = trimValue(input.primarySubnet)
	const additionalSubnets = (input.additionalSubnets || [])
		.filter((subnet) => subnet.cidr.trim() !== '')
		.map((subnet) => ({
			cidr: subnet.cidr.trim(),
			resourceType: subnet.resourceType,
			purpose: resolveSubnetPurpose(subnet.resourceType, subnet.purpose)
		}))

	const commonNetworkDetails = {
		commonNetworkResources: buildCommonNetworkResourcesPayload(input)
	}

	if (!isGke && input.nonGkeAddSubnets !== 'yes') {
		return {
			...commonNetworkDetails,
			subnetRequired: 'no',
			infrastructureType,
			primarySubnet: '',
			primarySubnetResourceType: '',
			primarySubnetPurpose: '',
			controlPlaneSubnet: '',
			secondaryPodsRange: '',
			secondaryServicesRange: '',
			additionalSubnets: []
		}
	}

	const hasNonGkeSubnetData =
		primarySubnet !== '' || additionalSubnets.length > 0
	const nextSubnetRequired = isGke || hasNonGkeSubnetData

	return {
		...commonNetworkDetails,
		subnetRequired: nextSubnetRequired ? 'yes' : 'no',
		infrastructureType,
		primarySubnet: nextSubnetRequired ? primarySubnet : '',
		primarySubnetResourceType:
			nextSubnetRequired && !isGke
				? trimValue(input.primarySubnetResourceType)
				: '',
		primarySubnetPurpose:
			nextSubnetRequired && !isGke
				? resolveSubnetPurpose(
						input.primarySubnetResourceType,
						input.primarySubnetPurpose
				  )
				: '',
		controlPlaneSubnet:
			nextSubnetRequired && isGke ? trimValue(input.controlPlaneSubnet) : '',
		secondaryPodsRange:
			nextSubnetRequired && isGke ? trimValue(input.secondaryPodsRange) : '',
		secondaryServicesRange:
			nextSubnetRequired && isGke
				? trimValue(input.secondaryServicesRange)
				: '',
		additionalSubnets: nextSubnetRequired ? additionalSubnets : []
	}
}

export const cidrRules = (
	label: string,
	required = true,
	resourceType = ''
) => ({
	...(required ? { required: `${label} is required` } : {}),
	validate: (value: string) => {
		if (!required && !value?.trim()) {
			return true
		}

		return getSubnetCidrError(value, resourceType, label) || true
	}
})
